/**
 * Edição do bloco HTML Legado.
 * @module components/Blocks/LegacyHTML/Edit
 */

import React, { Component } from 'react';
import loadable from '@loadable/component';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import { injectLazyLibs } from '@plone/volto/helpers/Loadable/Loadable';
import { userHasRoles } from '@plone/volto/helpers/User/User';
import InnerHTML from '@serpro/react-dangerously-set-html-content';
import cx from 'classnames';
import { withLgpdCookie } from 'govbr.lgpd.plugin/components';
import { blockHTML } from 'govbr.lgpd.plugin/helpers';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Button, Popup } from 'semantic-ui-react';

import clearSVG from '@plone/volto/icons/clear.svg';
import codeSVG from '@plone/volto/icons/code.svg';
import indentSVG from '@plone/volto/icons/indent.svg';
import showSVG from '@plone/volto/icons/show.svg';

const Editor = loadable(() => import('react-simple-code-editor'));

const messages = defineMessages({
  source: {
    id: 'Source',
    defaultMessage: 'Source',
  },
  preview: {
    id: 'Preview',
    defaultMessage: 'Preview',
  },
  placeholder: {
    id: '<p>Add some HTML here</p>',
    defaultMessage: '<p>Add some HTML here</p>',
  },
  prettier: {
    id: 'Prettify your code',
    defaultMessage: 'Prettify your code',
  },
  clear: {
    id: 'Clear',
    defaultMessage: 'Clear',
  },
  code: {
    id: 'Code',
    defaultMessage: 'Code',
  },
  LegacyHTMLWarning: {
    id: 'LegacyHTMLWarning',
    defaultMessage:
      'This block is read-only and does not allow editing. To update the migrated content, use the HTML block or other compatible blocks.',
  },
});

/**
 * Classe de edição do bloco HTML Legado.
 * @class Edit
 * @extends Component
 */
class Edit extends Component {
  static propTypes = {
    selected: PropTypes.bool.isRequired,
    block: PropTypes.string.isRequired,
    index: PropTypes.number.isRequired,
    data: PropTypes.objectOf(PropTypes.any).isRequired,
    onChangeBlock: PropTypes.func.isRequired,
    onSelectBlock: PropTypes.func.isRequired,
    onDeleteBlock: PropTypes.func.isRequired,
    handleKeyDown: PropTypes.func.isRequired,
    editable: PropTypes.bool,
  };

  static defaultProps = {
    editable: true,
  };

  constructor(props) {
    super(props);
    this.state = {
      isPreview: false,
      isNonManagerPreview: false,
    };
    this.onChangeCode = this.onChangeCode.bind(this);
    this.onPreview = this.onPreview.bind(this);
    this.onCodeEditor = this.onCodeEditor.bind(this);
    this.toggleNonManagerPreview = this.toggleNonManagerPreview.bind(this);
  }

  codeEditorRef = React.createRef();
  savedSelection = {};

  componentDidUpdate(prevProps, prevState, snapshot) {
    this.savedSelection = snapshot;

    this.codeEditorRef.current &&
      this.restoreSelectionAndFocus(this.codeEditorRef.current);
  }

  shouldComponentUpdate(nextProps) {
    if (!this._input) {
      return true;
    }

    return (
      this.props.selected ||
      !isEqual(this.props.data, nextProps.data) ||
      this._input.selectionStart !== this.savedSelection.selectionStart ||
      this._input.selectionEnd !== this.savedSelection.selectionEnd
    );
  }

  onChangeCode(code) {
    this.props.onChangeBlock(this.props.block, {
      ...this.props.data,
      html: code,
    });
  }

  getValue() {
    return this.props.data.html || '';
  }

  async onPreview() {
    try {
      const code = (
        await this.props.prettierStandalone.format(this.getValue(), {
          parser: 'html',
          plugins: [this.props.prettierParserHtml],
        })
      ).trim();
      this.setState(
        {
          isPreview: !this.state.isPreview,
        },
        () => this.onChangeCode(code),
      );
    } catch (ex) {
      // erro ao analisar o HTML digitado pelo usuário
    }
  }

  onPrettify = async () => {
    try {
      const code = (
        await this.props.prettierStandalone.format(this.getValue(), {
          parser: 'html',
          plugins: [this.props.prettierParserHtml],
        })
      ).trim();
      this.onChangeCode(code);
    } catch (ex) {
      // erro ao analisar o HTML digitado pelo usuário
    }
  };

  onCodeEditor() {
    this.setState({ isPreview: !this.state.isPreview });
  }

  toggleNonManagerPreview() {
    this.setState({ isNonManagerPreview: !this.state.isNonManagerPreview });
  }

  getSelection = (editor) => {
    if (!editor || !editor._input) {
      return {};
    }

    const o = {};
    if (editor._input.selectionStart) {
      o.selectionStart = editor._input.selectionStart;
    }
    if (editor._input.selectionEnd) {
      o.selectionEnd = editor._input.selectionEnd;
    }
    return o;
  };

  getSnapshotBeforeUpdate(prevProps, prevState) {
    return this.getSelection(this.codeEditorRef.current);
  }

  restoreSelectionAndFocus = (editor) => {
    if (
      this.props.selected &&
      editor._input &&
      typeof this.savedSelection?.selectionStart === 'number' &&
      typeof this.savedSelection?.selectionEnd === 'number'
    ) {
      editor._input.selectionStart = this.savedSelection?.selectionStart;
      editor._input.selectionEnd = this.savedSelection?.selectionEnd;
      editor._input.focus();
    }
  };

  render() {
    const classScriptMsg = cx(
      'text-center',
      'text-danger',
      // styles.securityWarning,
    );
    const placeholder =
      this.props.data.placeholder ||
      this.props.intl.formatMessage(messages.placeholder);
    const blockObject = blockHTML(
      this.getValue(),
      this.props.performanceCookies,
      this.props.thirdPartyCookies,
      this.props.isClient,
      this.props.groupDomains,
      { allowScriptContent: true },
    );
    const value = blockObject.html;

    // Usuários sem papel Manager veem o código HTML em uma caixa somente leitura,
    // mas com opção de alternar para a visualização renderizada.
    if (!userHasRoles(this.props.user, ['Manager'])) {
      return (
        <>
          <div className={classScriptMsg}>
            {this.props.intl.formatMessage(messages.LegacyHTMLWarning)}
          </div>
          {this.props.selected && value && (
            <div className="toolbar">
              <Popup
                trigger={
                  <Button
                    icon
                    basic
                    aria-label={this.props.intl.formatMessage(messages.source)}
                    active={!this.state.isNonManagerPreview}
                    onClick={this.toggleNonManagerPreview}
                  >
                    <Icon name={codeSVG} size="24px" />
                  </Button>
                }
                position="top center"
                content={this.props.intl.formatMessage(messages.code)}
                size="mini"
              />
              <Popup
                trigger={
                  <Button
                    icon
                    basic
                    aria-label={this.props.intl.formatMessage(messages.preview)}
                    active={this.state.isNonManagerPreview}
                    onClick={this.toggleNonManagerPreview}
                  >
                    <Icon name={showSVG} size="24px" />
                  </Button>
                }
                position="top center"
                content={this.props.intl.formatMessage(messages.preview)}
                size="mini"
              />
            </div>
          )}
          {this.state.isNonManagerPreview ? (
            value ? (
              <InnerHTML html={blockObject.html} />
            ) : (
              <InnerHTML
                html={this.props.intl.formatMessage(messages.placeholder)}
              />
            )
          ) : (
            <pre
              className="html-editor"
              aria-readonly="true"
              style={{ whiteSpace: 'pre-wrap', wordBreak: 'keep-all' }}
            >
              {this.getValue()}
            </pre>
          )}
        </>
      );
    }

    return (
      <>
        <div className={classScriptMsg}>
          {this.props.intl.formatMessage(messages.LegacyHTMLWarning)}
        </div>
        {this.props.selected && value && (
          <div className="toolbar">
            <Popup
              trigger={
                <Button
                  icon
                  basic
                  aria-label={this.props.intl.formatMessage(messages.source)}
                  active={!this.state.isPreview}
                  onClick={this.onCodeEditor}
                >
                  <Icon name={codeSVG} size="24px" />
                </Button>
              }
              position="top center"
              content={this.props.intl.formatMessage(messages.code)}
              size="mini"
            />
            <Popup
              trigger={
                <Button
                  icon
                  basic
                  aria-label={this.props.intl.formatMessage(messages.preview)}
                  active={this.state.isPreview}
                  onClick={this.onPreview}
                >
                  <Icon name={showSVG} size="24px" />
                </Button>
              }
              position="top center"
              content={this.props.intl.formatMessage(messages.preview)}
              size="mini"
            />
            <Popup
              trigger={
                <Button
                  icon
                  basic
                  aria-label={this.props.intl.formatMessage(messages.prettier)}
                  onClick={this.onPrettify}
                >
                  <Icon name={indentSVG} size="24px" />
                </Button>
              }
              position="top center"
              content={this.props.intl.formatMessage(messages.prettier)}
              size="mini"
            />
            <div className="separator" />
            <Popup
              trigger={
                <Button.Group>
                  <Button icon basic onClick={() => this.onChangeCode('')}>
                    <Icon name={clearSVG} size="24px" color="#e40166" />
                  </Button>
                </Button.Group>
              }
              position="top center"
              content={this.props.intl.formatMessage(messages.clear)}
              size="mini"
            />
          </div>
        )}
        {this.state.isPreview ? (
          value ? (
            <InnerHTML html={blockObject.html} />
          ) : (
            <InnerHTML
              html={this.props.intl.formatMessage(messages.placeholder)}
            />
          )
        ) : (
          <Editor
            value={this.getValue()}
            readOnly={!this.props.editable}
            placeholder={placeholder}
            onValueChange={(code) => this.onChangeCode(code)}
            highlight={
              this.props.prismCore?.highlight &&
              this.props.prismCore?.languages?.html
                ? (code) =>
                    this.props.prismCore.highlight(
                      code,
                      this.props.prismCore.languages.html,
                      'html',
                    )
                : () => {}
            }
            padding={8}
            className="html-editor"
            ref={(node) => {
              if (node) {
                this.codeEditorRef.current = node;
              }
            }}
            ignoreTabKey={true}
          />
        )}
      </>
    );
  }
}

const withPrismMarkup = (WrappedComponent) => (props) => {
  const [loaded, setLoaded] = React.useState();
  const promise = React.useRef(null);
  const cancelled = React.useRef(false);

  React.useEffect(() => {
    promise.current = import('prismjs/components/prism-markup');
    promise.current.then(() => {
      if (!cancelled.current) {
        setLoaded(true);
      }
    });
    return () => {
      cancelled.current = true;
    };
  }, []);

  return loaded ? <WrappedComponent {...props} /> : null;
};

export default compose(
  connect((state) => ({ user: state.users.user })),
  injectLazyLibs(['prettierStandalone', 'prettierParserHtml', 'prismCore']),
  withPrismMarkup,
  injectIntl,
  withLgpdCookie,
)(Edit);
